import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
	useRef
} from 'react';
import { TimkenUnit } from '../../../../components/bearing-search/types/types';
import greaseFillBySpecsReducer, { initialFillBySpecsState } from './reducer';
import { bearingTypes } from '../../../../components/bearing-search/api/api';
import SelectDropdown from '../../../../components/bearing-search/components/forms/SelectDropdown';
import { text, unitList, unitMap } from '../../../data';
import { SelectOption } from '../../../types/types';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import RelubricateConditions from '../relubricate-conditions/RelubricateConditions';
import { GreaseFrequencyConditionsPayload } from '../relubricate-conditions/reducer';
import { GreaseFrequencyPartPayload } from '../GreaseRelubrication';

interface RelubricateBySpecificationProps {
	typeCode: string;
	onSubmit: ( payload: GreaseFrequencyPartPayload ) => void;
	onClear: () => void;
}

const RelubricateBySpecification: React.FC<RelubricateBySpecificationProps> = ( {
	typeCode,
	onSubmit,
	onClear,
} ) => {
	const handleFormChangeRef = useRef<any>(null);
	const [ isValidated, setIsValidated ] = useState( false );
	const [ stamp, setStamp ] = useState( '' );
	const [ relubricationData, setRelubricationData ] =
		useState<GreaseFrequencyConditionsPayload>();

	const [ formState, dispatch ] = useReducer(
		greaseFillBySpecsReducer,
		initialFillBySpecsState,
	);

	useEffect( () => {
		setIsValidated( false );
	}, [ typeCode ] );

	const selectBearingType = ( event: ChangeEvent<HTMLSelectElement> ) => {
		const partType = event.target.value as string;

		const selected = bearingTypes.find( ( type ) => type.partType === partType );

		if ( selected ) {
			dispatch( {
				type: 'change_bearing_type',
				payload: selected,
			} );
		}
	};

	const clearSearch = () => {
		setIsValidated( false );

		setStamp( new Date().getTime().toString() );

		dispatch( {
			type: 'reset',
		} );

		onClear();
	};

	const handlePartChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_part_data',
		} );
	};

	const handleFormChange = ( payload?: GreaseFrequencyConditionsPayload ) => {
		setIsValidated( false );
		setRelubricationData( payload );
	};

	const handleCalculate = ( event: FormEvent ) => {
		event.preventDefault();

		setIsValidated( true );

		if ( formState.partData && relubricationData ) {
			onSubmit( {
				...formState.partData,
				...relubricationData,
			} );
		}
	};

	const handleUnitChange = ( event: ChangeEvent<HTMLSelectElement> ) => {
		setIsValidated( false );

		dispatch( {
			type: 'change_unit',
			payload: event.target.value as TimkenUnit,
		} );

		handleFormChangeRef.current?.handleFormChange(event.target.value);
	};

	return (
		<div>
			<form onChange={handlePartChange}>
				<div className="row form-row">
					<div className="col-md-3">
						<SelectDropdown
							id="grease_relubricate_bearing_type"
							required={true}
							label={text.bearingType}
							value={formState.partType}
							options={bearingTypes.map( ( type ) => {
								return {
									id: type.partType,
									label: type.description,
									value: type.partType,
								} as SelectOption<string>;
							} )}
							onChange={selectBearingType}
						/>
					</div>

					<div className="col-md-2">
						<SelectDropdown
							id="grease_relubricate_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={handleUnitChange}
						/>
					</div>
				</div>

				<div className="row form-row">
					<div className="col-md-2">
						<NumberInput
							id="grease_relubricate_inner_diameter"
							required={true}
							label={text.innerDiameter}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.innerDiameter}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'innerDiameter' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'innerDiameter', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="grease_relubricate_outer_diameter"
							required={true}
							label={text.outerDiameter}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.outerDiameter}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'outerDiameter' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'outerDiameter', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="grease_relubricate_width"
							required={true}
							label={text.bearingWidth}
							unitLabel={unitMap[ formState.unit ].length}
							value={formState.bearingWidth}
							min={0}
							invalid={isValidated && ! formState.validation.get( 'bearingWidth' )}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'bearingWidth', event.target.value ],
								} )
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="grease_relubricate_weight"
							required={true}
							label={text.bearingWeight}
							unitLabel={unitMap[ formState.unit ].weight}
							value={formState.bearingWeight}
							min={0}
							invalid={
								isValidated && ! formState.validation.get( 'bearingWeight' )
							}
							errorMessage={text.errorNonZero}
							onChange={( event: ChangeEvent<HTMLInputElement> ) =>
								dispatch( {
									type: 'change_number_field',
									payload: [ 'bearingWeight', event.target.value ],
								} )
							}
						/>
					</div>
				</div>
			</form>

			<RelubricateConditions
				key={stamp}
				unit={formState.unit}
				isValidated={isValidated}
				onChange={handleFormChange}
				onSubmit={handleCalculate}
				ref={handleFormChangeRef}
			/>

			<form onSubmit={handleCalculate}>
				<div>
					<div  className="t-engineering-tools__terms-of-use"
						  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
					/>
					<div className="t-engineering-tools__search-actions">
						<button
							className="btn btn--primary"
							type="submit"
							disabled={isValidated}
						>
							{text.calculate}
						</button>

						<button
							className="btn btn--secondary"
							type="button"
							onClick={() => clearSearch()}
						>
							{text.clear}
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default RelubricateBySpecification;
